import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import BatchScheduleTeacherResults from './BatchScheduleTeacherResults';
import { batchesAPI } from '../services/batches';
import { parseSubstituteTeachersResponse } from './Teacher/SubstituteTeacherPicker';
import { rebucketTeachersForWindow } from '../utils/teacherWindowFit';

export default function FindFreeTeachersDialog({
  open,
  onClose,
  schedules = [],
  timeLabel = '',
  onCreateWithTeacher,
}) {
  const [exact, setExact] = useState([]);
  const [partial, setPartial] = useState([]);
  const [unavailable, setUnavailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const scheduleKey = JSON.stringify(schedules || []);
  const parsedSchedules = useMemo(
    () => (scheduleKey ? JSON.parse(scheduleKey) : []),
    [scheduleKey]
  );
  const selectedDays = useMemo(
    () => [...new Set(parsedSchedules.map((s) => Number(s.day_of_week)))],
    [parsedSchedules]
  );
  const usesHourSlots = useMemo(() => {
    return parsedSchedules.some((slot) => {
      const start = String(slot.start_time || '').slice(0, 5);
      const end = String(slot.end_time || '').slice(0, 5);
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      if (![sh, sm, eh, em].every(Number.isFinite)) return false;
      return eh * 60 + em - (sh * 60 + sm) >= 60;
    });
  }, [parsedSchedules]);

  useEffect(() => {
    if (!open) return undefined;
    setSelectedId('');
    setError('');
    const parsedSchedules = scheduleKey ? JSON.parse(scheduleKey) : [];
    if (!parsedSchedules.length) {
      setExact([]);
      setPartial([]);
      setUnavailable([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    batchesAPI
      .getAvailableTeachersForSchedules(parsedSchedules, { findClassSlotsInWindow: true })
      .then((response) => {
        if (cancelled) return;
        const parsed = parseSubstituteTeachersResponse(response);
        const grouped = rebucketTeachersForWindow(
          parsed.available || [],
          parsed.partial || [],
          parsed.unavailable || [],
          selectedDays
        );
        setExact(grouped.exact);
        setPartial(grouped.partial);
        setUnavailable(grouped.unavailable);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setExact([]);
        setPartial([]);
        setUnavailable([]);
        if (status === 403) {
          setError('You do not have permission to search teachers.');
        } else {
          setError(err?.response?.data?.message || 'Could not load teachers for this time.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, scheduleKey, selectedDays]);

  const selectedTeacher = [...exact, ...partial, ...unavailable].find(
    (t) => String(t.id) === String(selectedId)
  );

  const handleCreate = (teacher) => {
    if (typeof onCreateWithTeacher === 'function') {
      onCreateWithTeacher(teacher || null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Find a free teacher</DialogTitle>
      <DialogContent dividers>
        {timeLabel ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {usesHourSlots
              ? `Looking for 1-hour classes inside ${timeLabel}. A teacher is listed if they have at least one free hour.`
              : `Looking for teachers free at ${timeLabel}.`}
          </Typography>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <BatchScheduleTeacherResults
            exact={exact}
            partial={partial}
            unavailable={unavailable}
            loading={loading}
            value={selectedId}
            onChange={setSelectedId}
            allowEmpty={false}
            emptyMessage="No teachers found for this time."
            variant="cards"
            confirmIfIncomplete={false}
            showSelectionWarnings={false}
            exactTitle={
              usesHourSlots
                ? 'Best fit — same 1 hour on all days'
                : 'Free at this time on all days'
            }
            partialTitle={
              usesHourSlots
                ? 'Free for some hours in this window'
                : 'Free on some days'
            }
            unavailableTitle="Not free in this window"
            emptyExactText={
              usesHourSlots
                ? 'No one is free at the same 1-hour slot on every selected day.'
                : 'No one is free at this time on every selected day.'
            }
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={() => handleCreate(null)}>Create without a teacher</Button>
        <Button
          variant="contained"
          disabled={!selectedTeacher}
          onClick={() => handleCreate(selectedTeacher)}
        >
          {selectedTeacher ? `Create batch with ${selectedTeacher.name}` : 'Create batch with this teacher'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
